import { GeneralService } from './../../service/general.service';
import { Component, OnInit, Input, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators} from '@angular/forms';
import { AuthenticationService } from '../../service/authentication.service';
import { EventService } from '../../service/event.service';
import { of }   from 'rxjs';
import { Router } from '@angular/router';
import { catchError, tap } from 'rxjs/operators';
import { LoginState } from '../../model/LoginState';
import { passwordValidator } from '../../validator/user-form-validator';
import { emptyValidator } from 'src/app/validator/empty-form-validator';

//local enum for login component
export enum ForgetPwdState {
  //get user name and generate security token
  Stage1 = "Stage1", 
  //get security token and new password
  Stage2 = "Stage2",
}

//local validator
export function checkfIfPasswordTheSame(group: FormGroup) {
  if(group.get('forgotPwdPassword').value && group.get('forgotPwdConfirmPassword').value){
    return group.get('forgotPwdPassword').value === group.get('forgotPwdConfirmPassword').value ? null : {passwordNotSame: true};
  }else{
    return null;
  }
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit {
  @Input() loginState: LoginState = LoginState.Welcome;
  inProgress: boolean = false;
  LoginStateEnum = LoginState;
  systemCenterProductVersion: any = {name: "System Center", version: ""};
  pwdResetInProgress: boolean = false;
  ForgetPwdStateEnum = ForgetPwdState;
  forgetPwdState: ForgetPwdState = ForgetPwdState.Stage1;
  showForgotPwdSecurityToken: boolean = false;
  showForgotPwdPassword: boolean = false;
  showForgotPwdConfirmPassword: boolean = false;
  isErrorGenSecurityToken: boolean = false;
  inGenSecurityTokenProgress: boolean = false;
  isErrorPwdReset: boolean = false;
  inPwdResetProgress: boolean = false;

  //login form
  loginForm = new FormGroup({
    username: new FormControl('', [
      Validators.required, emptyValidator()
    ]),
    password: new FormControl('', [
      Validators.required
    ])
  });

  //forgot password form
  forgotPwdForm = new FormGroup({
    forgotPwdUsername: new FormControl('', [
      Validators.required, emptyValidator()
    ]),
    isSecurityTokenGenerated: new FormControl<boolean>(false, [
    ]),
    forgotPwdSecurityToken: new FormControl('', [
      Validators.required
    ]),
    forgotPwdPassword: new FormControl('', [
      Validators.required,
      passwordValidator()
    ]),
    forgotPwdConfirmPassword: new FormControl('', [
      Validators.required
    ])
  }, {
    validators: checkfIfPasswordTheSame
  });

  constructor(private authService: AuthenticationService,
              private eventService: EventService,
              private router: Router,
              private genService: GeneralService,
              private renderer: Renderer2) {
              }

  //member functions
  get username() { return this.loginForm.get('username'); }
  get password() { return this.loginForm.get('password'); }
  get forgotPwdUsername() { return this.forgotPwdForm.get('forgotPwdUsername'); }
  get isSecurityTokenGenerated () { return this.forgotPwdForm.get('isSecurityTokenGenerated'); }
  get forgotPwdSecurityToken() { return this.forgotPwdForm.get('forgotPwdSecurityToken'); }
  get forgotPwdPassword() { return this.forgotPwdForm.get('forgotPwdPassword'); }
  get forgotPwdConfirmPassword() { return this.forgotPwdForm.get('forgotPwdConfirmPassword'); }

  ngOnInit(): void {
    this.genService.getSystemCenterProductVersion().pipe(
      tap(resp => {
        this.systemCenterProductVersion = resp.body;
      }),
      catchError(val => {
        return of(val);
      })
    ).subscribe();

    if(this.authService.isLoggedIn()){
      this.router.navigate(['home']);
    }else{
      setTimeout(() => this.renderer.selectRootElement('#username').focus(), 0);
    }
  }

  ngOnDestroy() {
  }

  //methods
  generateSecurityToken(): void {
    //security token is already generated
    if(this.isSecurityTokenGenerated.value == true){
      //go to stage 2
      this.forgetPwdState = ForgetPwdState.Stage2;
      this.isErrorGenSecurityToken = false;
      setTimeout(() => this.renderer.selectRootElement('#forgotPwdSecurityToken').focus(), 0);
    }else{
      //generate security token
      this.isErrorGenSecurityToken = false;
      this.inGenSecurityTokenProgress = true;
      this.authService.generateSecurityToken(this.forgotPwdUsername.value).pipe(
        tap(resp => {
          //go to stage 2
          this.forgetPwdState = ForgetPwdState.Stage2;
          this.isErrorGenSecurityToken = false;
          setTimeout(() => this.renderer.selectRootElement('#forgotPwdSecurityToken').focus(), 0);
        }),
        catchError(val => {
          //show error
          this.isErrorGenSecurityToken = true;
          this.inGenSecurityTokenProgress = false;
          return of(val);
        })
      ).subscribe();
    }
  }

  forgetPwd(newState:LoginState): void {
    this.loginState = newState;
    setTimeout(() => this.renderer.selectRootElement('#forgotPwdUsername').focus(), 0);
  }

  forgetPwdGoBack(): void {
    if(this.forgetPwdState === ForgetPwdState.Stage1){
      this.loginState= LoginState.Welcome;
      this.forgotPwdForm.reset();
      this.isErrorGenSecurityToken = false;
      setTimeout(() => this.renderer.selectRootElement('#username').focus(), 0);
    }else if(this.forgetPwdState === ForgetPwdState.Stage2){
      this.forgetPwdState = ForgetPwdState.Stage1;
      this.forgotPwdSecurityToken.reset();
      this.forgotPwdPassword.reset();
      this.forgotPwdConfirmPassword.reset();
      this.isErrorPwdReset = false;
      setTimeout(() => this.renderer.selectRootElement('#forgotPwdUsername').focus(), 0);
    }
  }

  onSubmit():void {
    this.inProgress = true;
    this.authService.login(this.loginForm.value.username, this.loginForm.value.password).pipe(tap(resp => {
      this.inProgress = false;
      if(this.loginState === LoginState.Forbidden){
        this.router.navigate(['login']);
      }
      this.eventService.broadcastLoginStateChange(LoginState.SignedIn);
    }),
      catchError(resp => {
        //show errors
        this.inProgress = false;

        if(resp.status == 401){
          this.loginState = LoginState.AccessError;
        //manager is not running or run into an internal error
        }else if(resp.status == 0 || resp.status == 404 || resp.status >= 500){
          this.loginState = LoginState.ServerError;
        }else{
          //forbidden
          this.loginState = LoginState.InvalidUsernameOrPassword;
        }
        
        return of(resp);
      })
    ).subscribe();
  }

  onResetPassword():void {
    if(this.forgetPwdState == ForgetPwdState.Stage1){
      this.generateSecurityToken();
    } else {
      this.isErrorPwdReset = false;
      this.inPwdResetProgress = true;
      this.authService.resetPassword(this.forgotPwdUsername.value, this.forgotPwdPassword.value, this.forgotPwdSecurityToken.value).pipe(
        tap(resp => {
          this.loginState = LoginState.PasswordWasReset;
          this.forgetPwdState = ForgetPwdState.Stage1; //reset to stage 1
          this.forgotPwdForm.reset();
          this.inPwdResetProgress = false;
        }),
        catchError(val => {
          //show error
          this.isErrorPwdReset = true;
          this.inPwdResetProgress = false;
          return of(val);
        })
      ).subscribe();
    }
  }
}
